import React, { JSX, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';
import pluralize from 'pluralize';
import fileDownload from 'react-file-download';
import { useSelector } from 'react-redux';
import usePlacesAutocomplete, * as UPA from 'use-places-autocomplete';
import * as RE from '~/reducers/experiences';
import { theme } from '~/theme';

import {
  Alert,
  Box,
  Pagination,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import BulkCategoryUpdateDialog from '~/components/Experiences/BulkCategoryUpdateDialog/BulkCategoryUpdateDialog';
import { Card, FlexWrapper, GridWrapper, Spinner } from '~/components/Experiences/components';

import { FILTER_RANGE_KEYS, FilterKeys } from '~/consts/experiences';

import useAppNavigation from '~/hooks/useAppNavigation';

import * as ES from '~/services/ExperiencesService';
import { ExperienceOffer } from '~/services/ExperiencesService';
import { downloadExperiencesOffersReport } from '~/services/ReportingService';

import { addQuery } from '~/utils/url';

import { ExperienceCardContent, ExperienceRowContent } from './Content';
import { FieldValueParams, Toggle } from './Experiences';
import { ExperiencesFormFilters, SearchModeOptions } from './ExperiencesFormFilters';

// Melbourne coordinates
const INITIAL_LOCATION = {
  coordinates: { lat: -37.828825993729765, lng: 144.9598820849199 },
  distance: '629KM',
};

const SIZE_PER_PAGE = 100;

const toggleOptions = ['card', 'table'];

export function ExperiencesContent(): JSX.Element {
  const { push } = useAppNavigation();
  const tenant = useSelector((state: App.State) => state.tenant);
  const [toggle, setToggle] = useState<Toggle>({ table: true });
  const [experiences, experiencesDispatch] = useReducer(RE.fetchExperiencesReducer, RE.initialExperiences);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [fieldSearchValue, setFieldSearchValue] = useState('');
  const [downloadingOffersReport, setDownloadingOffersReport] = useState<string | undefined>();
  const cancelFetchRef = useRef<() => void | undefined>(undefined);

  const countExp = pluralize('experience', experiences?.total || 0, true);
  const experiencesTitle = `${countExp} found by your search criteria`;

  const openExperienceDetails = (experience: ExperienceOffer) => {
    window.open(`/experience/${experience.id}`, '_blank');
  };
  const pageLength = Math.ceil(experiences?.total / SIZE_PER_PAGE) ?? 1;

  const notFoundExperiences: boolean = !experiences.error && !experiences.loading && experiences.items?.length === 0;

  const { enqueueSnackbar } = useSnackbar();

  const columns: Array<string> = [
    'Status',
    'Image',
    'Name & Description',
    'Provider Price',
    'Sale Price',
    'Margin',
    'Price Overridden',
    'Brands',
    'Reviews Rating',
    'Categories',
    'Curation Comment',
  ];

  const hasExperiences = experiences.items?.length > 0;

  const [categories, categoriesDispatch] = useReducer(RE.fetchCategoriesReducer, RE.initialCategories);
  const [searchModeSelected, setSearchModeSelected] = useState(SearchModeOptions.TITLE);
  const [page, setPage] = useState<number>(1);

  const [filters, setFilters] = useState<ES.Filters>({
    ...ES.initialFiltersValues,
    ...INITIAL_LOCATION,
  });

  const actions = useMemo(
    () => [
      {
        label: 'Bulk Update Categories',
        loading: false,
        disabled: false,
        onClick: () => setBulkCategoryDialogOpen(true),
      },
      {
        label: 'Download XLSX',
        loading: downloadingOffersReport === 'xlsx',
        disabled: !!downloadingOffersReport,
        onClick: () => downloadOffersReport('xlsx'),
      },
      {
        label: 'Download CSV',
        loading: downloadingOffersReport === 'csv',
        disabled: !!downloadingOffersReport,
        onClick: () => downloadOffersReport('csv'),
      },
    ],
    [downloadingOffersReport],
  );

  const canFetchExperiences =
    searchModeSelected != SearchModeOptions.LOCATION ||
    (filters.coordinates && !!filters.distance && filters.distance !== '0KM');

  const {
    ready,
    setValue,
    clearSuggestions,
    suggestions: { status, data },
  } = usePlacesAutocomplete({ debounce: 300 });

  const resetFilters = useCallback(() => {
    setFilters((filter) => {
      const resetedFilters = {
        ...ES.initialFiltersValues,
        distance: filter.distance,
        coordinates: filter.coordinates,
      };

      Object.values(SearchModeOptions).forEach((key) => (resetedFilters[key] = filter[key] ?? null));

      return resetedFilters;
    });
  }, [filters]);

  const onChangeSearchValue = (fieldSearchValue: string, searchModeSelected: string) => {
    if (searchModeSelected !== SearchModeOptions.LOCATION) {
      Object.values(SearchModeOptions).forEach((key) => delete filters[key]);
      filters[searchModeSelected] = fieldSearchValue;
      refetchExperiences();
      setFilters(filters);
    }
  };

  const refetchExperiences = useCallback(() => {
    deboucedFetchExperiences(filters, 1);
  }, [filters]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    deboucedFetchExperiences(filters, page);
    if (page !== 1) {
      setPage(1);
    }
  }, [filters]);

  useEffect(() => {
    if (canFetchExperiences) {
      deboucedFetchExperiences(filters, page);
    }
  }, [filters.distance, filters.coordinates]);

  useEffect(() => {
    if (canFetchExperiences) {
      deboucedFetchExperiences(filters, page);
    }
  }, [page]);

  useEffect(() => {
    fetchCategories();
    if (canFetchExperiences) {
      deboucedFetchExperiences(filters, page);
    }
  }, [tenant]);

  useEffect(() => {
    onChangeSearchValue(fieldSearchValue, searchModeSelected);
  }, [fieldSearchValue]);

  useEffect(() => {
    setFieldSearchValue('');

    setFilters((filters) => {
      if (
        searchModeSelected === SearchModeOptions.ID ||
        searchModeSelected === SearchModeOptions.TITLE ||
        searchModeSelected === SearchModeOptions.VENDOR
      ) {
        filters.coordinates = null;
        filters.distance = null;
      } else if (searchModeSelected === SearchModeOptions.LOCATION) {
        filters[SearchModeOptions.ID] = null;
        filters[SearchModeOptions.TITLE] = null;
        filters[SearchModeOptions.VENDOR] = null;
      }

      return filters;
    });
  }, [searchModeSelected]);

  const handleToggle = useCallback((opt: string) => setToggle({ [opt]: true }), []);

  const searchOptions: Array<string> = useMemo(
    () => (status === 'OK' ? data?.map((option) => option?.description) : []),
    [status, data],
  );

  const setFiltersByKey = useCallback(
    (key: FilterKeys, value: string) => {
      const items = filters[key] ?? [];
      setFilter(key, items.includes(value) ? items.filter((item) => item !== value) : [...items, value]);
    },
    [filters.categories, filters.status, filters.providerIn],
  );

  const handleFieldValue = useCallback(
    (params: FieldValueParams) => {
      const { key, range, fieldValue } = params;

      if (FILTER_RANGE_KEYS.includes(key)) {
        setFilters((filters) => ({
          ...filters,
          [key]: { ...filters[key], [range]: fieldValue },
        }));
      }
    },
    [filters.price, filters.rating],
  );

  const fetchCategories = async (): Promise<void> => {
    categoriesDispatch({ type: 'LOAD' });
    try {
      const res = await ES.getCategories(tenant.brand);

      if (res?.errors?.length > 0) {
        categoriesDispatch({
          type: 'ERROR',
          message: "[ERROR] Don't exists categories response.",
        });
      }

      categoriesDispatch({
        type: 'FETCH',
        payload: res.result,
      });
    } catch (error) {
      console.error('ERROR ON FETCH CATEGORIES: ', error);
      categoriesDispatch({ type: 'ERROR', message: error.message });
    }
  };

  const fetchExperiences = async (filters: ES.Filters, page: number): Promise<void> => {
    experiencesDispatch({ type: 'LOAD' });
    try {
      const offset: number = (page - 1) * SIZE_PER_PAGE;

      const res = await ES.getExperienceOffers({
        curationData: true,
        offset,
        filters,
        limit: SIZE_PER_PAGE,
        brand: tenant.brand,
        getController: (controller) => {
          if (cancelFetchRef.current) {
            cancelFetchRef.current();
          }
          cancelFetchRef.current = controller.cancel;
        },
      });

      if (res?.errors?.length > 0) {
        experiencesDispatch({
          type: 'ERROR',
          message: "[ERROR] Experiences response doesn't exist.",
        });
      }

      experiencesDispatch({
        type: 'FETCH',
        total: res?.total,
        payload: res.result,
      });
    } catch (error) {
      console.error('ERROR ON FETCH EXPERIENCES: ', error);
      experiencesDispatch({
        type: 'ERROR',
        message: error.message,
      });
    }
  };

  const deboucedFetchExperiences = useMemo(() => debounce(fetchExperiences, 1000), [tenant.brand]);

  const onSelectLocation = useCallback(async (address: string) => {
    clearSuggestions();
    setFieldSearchValue(address);
    const position = await UPA.getGeocode({ address });
    const coordinates = await UPA.getLatLng(position[0]);

    setFilter('coordinates', coordinates);
  }, []);

  const handleSearchFieldValue = useCallback((fieldValue: string) => {
    setValue(fieldValue);
    setFieldSearchValue(fieldValue);
  }, []);

  const onPageChange = (page: number): void => {
    const { search } = addQuery(location, { page });
    push(search);
    setPage(page);
  };

  const fetchExperiencesWithFilters = useCallback(() => {
    if (!canFetchExperiences) {
      return;
    }

    onPageChange(1);
    deboucedFetchExperiences(filters, 1);
  }, [filters]);

  const handleSearchModeSelected = useCallback((event: SelectChangeEvent) => {
    setSearchModeSelected(event.target.value as SearchModeOptions);
  }, []);

  const setFilter = useCallback((name: keyof ES.Filters, value: unknown) => {
    setFilters((filters) => ({ ...filters, [name]: value }));
  }, []);

  const setGoLiveDateFilter = useCallback(
    (value: ES.GoLiveDateInWeeks | null) => {
      setFilter('goLiveDateInWeeks', value);
    },
    [setFilter],
  );

  const downloadOffersReport = useCallback((format) => {
    setDownloadingOffersReport(format);
    downloadExperiencesOffersReport({ format })
      .then((data) => {
        const fileName = `experience-live-report.${format}`;
        fileDownload(data, fileName);
        enqueueSnackbar('Success', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Error', { variant: 'error' });
      })
      .finally(() => {
        setDownloadingOffersReport(undefined);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePage = useCallback((_, page: number) => setPage(page), []);

  return (
    <>
      <Box mt={4}>
        <ExperiencesFormFilters
          ready={ready}
          filters={filters}
          categories={categories}
          toggleSelected={toggle}
          resetFilters={resetFilters}
          toggleOptions={toggleOptions}
          onSelectToggle={handleToggle}
          searchOptions={searchOptions}
          setFiltersByKey={setFiltersByKey}
          fieldSearchValue={fieldSearchValue}
          handleFieldValue={handleFieldValue}
          onSelectLocation={onSelectLocation}
          handleSearchFieldValue={handleSearchFieldValue}
          fetchExperiencesWithFilters={fetchExperiencesWithFilters}
          searchModeSelected={searchModeSelected}
          setSearchModeSelected={handleSearchModeSelected}
          showGoLiveDateFilter
          setGoLiveDateFilter={setGoLiveDateFilter}
          actions={actions}
        />
      </Box>

      <FlexWrapper>
        {experiences?.loading && <Spinner />}

        {hasExperiences && <Alert severity="success">{experiencesTitle}</Alert>}

        {experiences?.error && <Alert severity="error">ERROR ON FETCH EXPERIENCES</Alert>}

        {notFoundExperiences && <Alert severity="warning">NO EXPERIENCES FOUND</Alert>}
      </FlexWrapper>

      {hasExperiences && (
        <FlexWrapper direction="column">
          {toggle?.table && (
            <FlexWrapper mt="10px">
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column}
                          sx={{
                            padding: 1,
                            fontSize: 16,
                            color: 'white',
                            backgroundColor: theme.palette.primary.dark,
                          }}
                        >
                          {column}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {experiences.items?.map((experience: ExperienceOffer) => (
                      <ExperienceRowContent
                        key={experience.id}
                        experience={experience}
                        onClick={() => openExperienceDetails(experience)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </FlexWrapper>
          )}

          {toggle?.card && (
            <GridWrapper columns="repeat(4, 1fr)">
              {experiences.items?.map((experience: ExperienceOffer) => (
                <Card key={experience.id}>
                  <ExperienceCardContent experience={experience} onClick={() => openExperienceDetails(experience)} />
                </Card>
              ))}
            </GridWrapper>
          )}
          <Stack spacing={2} mt={4}>
            <Pagination
              hidePrevButton
              hideNextButton
              count={pageLength}
              page={page}
              onChange={handlePage}
              color="primary"
            />
          </Stack>
        </FlexWrapper>
      )}

      <BulkCategoryUpdateDialog
        open={bulkCategoryDialogOpen}
        onClose={() => setBulkCategoryDialogOpen(false)}
        categories={categories.items ?? []}
      />
    </>
  );
}
